import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { createTeamCheckoutSession } from '@healthshare/shared/commands/billing'
import { requireAuth } from '../wrappers/requireAuth'
import { useBackendApi } from '../hooks/useBackendApi'

const PER_USER_PRICE = 19
// Display only — the authoritative charge is the Stripe annual Price. Keep this in
// sync with the STRIPE_TEAM_ANNUAL_PRICE_ID amount (a flat $205/yr per seat, ~10%
// off the $228/yr you'd pay monthly).
const ANNUAL_PER_USER_PRICE = 205

function TeamSignup() {
  const api = useBackendApi()
  const [searchParams] = useSearchParams()
  const isUpgrade = searchParams.get('upgrade') === '1'
  const [orgName, setOrgName] = useState('')
  const [quantity, setQuantity] = useState(1)
  const [interval, setInterval] = useState<'month' | 'year'>('month')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string>()

  const isAnnual = interval === 'year'
  const perUserPrice = isAnnual ? ANNUAL_PER_USER_PRICE : PER_USER_PRICE
  const intervalSuffix = isAnnual ? '/yr' : '/mo'
  const total = perUserPrice * quantity

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!orgName.trim()) {
      setError('Please enter your organization name')
      return
    }
    if (quantity < 1) {
      setError('You need at least 1 covered employee')
      return
    }

    setSubmitting(true)
    setError(undefined)

    try {
      const { url } = await createTeamCheckoutSession(api, {
        successUrl: window.location.origin,
        cancelUrl: window.location.href,
        orgName: orgName.trim(),
        quantity,
        isUpgrade,
        billingInterval: interval,
      })
      window.location.href = url
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong')
      setSubmitting(false)
    }
  }

  return (
    <div className="row mb-5">
      <div className="col-12 col-md-8 col-lg-6">
        <h1>{isUpgrade ? 'Upgrade to Team Plan' : 'Team Plan'}</h1>
        <p className="text-muted">
          Cover your employees with a single team subscription. Includes all Family plan features.
        </p>

        {isUpgrade && (
          <div className="alert alert-info">
            Your personal subscription will be cancelled when checkout completes,
            and you'll receive a prorated credit for unused time on your next invoice.
          </div>
        )}

        <div className="card mb-4">
          <div className="card-body">
            <h5 className="card-title">Pricing</h5>
            <div className="btn-group mb-3" role="group" aria-label="Billing interval">
              <button
                type="button"
                className={`btn ${isAnnual ? 'btn-outline-primary' : 'btn-primary'}`}
                onClick={() => setInterval('month')}
                aria-pressed={!isAnnual}
              >
                Monthly
              </button>
              <button
                type="button"
                className={`btn ${isAnnual ? 'btn-primary' : 'btn-outline-primary'}`}
                onClick={() => setInterval('year')}
                aria-pressed={isAnnual}
              >
                Annual (save 10%)
              </button>
            </div>
            <p className="mb-0"><strong>${perUserPrice}{intervalSuffix}</strong> per covered employee</p>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="orgName" className="form-label">Organization Name</label>
            <input
              id="orgName"
              type="text"
              className="form-control"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Acme Corp"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="quantity" className="form-label">Number of Covered Employees</label>
            <input
              id="quantity"
              type="number"
              className="form-control"
              value={quantity}
              onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
              min={1}
              max={1000}
              required
            />
          </div>

          <div className="card mb-4 bg-light">
            <div className="card-body">
              <div className="d-flex justify-content-between">
                <span>{quantity} employee{quantity !== 1 ? 's' : ''} &times; ${perUserPrice}{intervalSuffix}</span>
                <span>${total}{intervalSuffix}</span>
              </div>
              <hr />
              <div className="d-flex justify-content-between">
                <strong>Total</strong>
                <strong>${total}{intervalSuffix}</strong>
              </div>
              {isAnnual && (
                <p className="text-success small mb-0 mt-2">Save 10% vs monthly</p>
              )}
            </div>
          </div>

          {error && <div className="alert alert-danger">{error}</div>}

          <button
            type="submit"
            className={`btn btn-primary btn-lg ${submitting ? 'disabled' : ''}`}
            disabled={submitting}
          >
            {submitting && (
              <span className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </span>
            )}
            Continue to Payment
          </button>
        </form>
      </div>
    </div>
  )
}

export default requireAuth(TeamSignup)
